import { _ as _object_spread } from "@swc/helpers/_/_object_spread";
import { jsx as _jsx } from "react/jsx-runtime";
import { useMemo } from 'react';
import { useNavigate } from 'react-router-dom';
import { useOverlayContext } from 'seagull';
import { getTestIds } from 'seagull/utils/TestIds';
import { routes } from '~/constants';
import { useIoContext } from '~/contexts';
import { useIntegrations } from '~/hooks/reactQuery/queries/useIntegrations';
import { useLocaleKeys } from '~/i18n';
import { integrationModel } from '~/models';
import { useBusinessChannelEvent } from '~/pusher';
import { PusherEvent } from '~/pusher/model';
import { useStoreActions } from '~/store/store-context';
import { IntegrationStatus } from '~/types/api.model';
import IntegrationCard from '../IntegrationCard/IntegrationCard';
import XeroLogo from '../logos/xero.svg';
export const GetXeroCardTID = (parentTestId)=>getTestIds({
        parentTestId,
        deprecatedTestIds: {
            XeroCardTID: {
                Card: 'xero-card',
                ConnectionInProgress: 'xero-card-connection-in-progress',
                Connect: 'xero-card-connect-button',
                Manage: 'xero-card-manage-button'
            }
        }
    });
const connectionInProgressCardProps = (param)=>{
    let { xeroTexts, testIds } = param;
    return {
        subTitle: xeroTexts.setupSubTitle(),
        mainAction: /*#__PURE__*/ _jsx(IntegrationCard.MainActionButton, {
            bi: {
                name: 'xero_connection_in_progress'
            },
            loading: true,
            testId: testIds.ConnectionInProgress(),
            children: xeroTexts.connectionInProgress()
        })
    };
};
const connectCardProps = (param)=>{
    let { handleConnect, xeroTexts, testIds } = param;
    return {
        subTitle: xeroTexts.connectSubTitle(),
        mainAction: /*#__PURE__*/ _jsx(IntegrationCard.MainActionButton, {
            bi: {
                name: 'connect_xero'
            },
            testId: testIds.Connect(),
            onClick: handleConnect,
            children: xeroTexts.connect()
        })
    };
};
const manageCardProps = (param)=>{
    let { companyName, handleManage, xeroTexts, testIds } = param;
    return {
        subTitle: xeroTexts.manageSubTitle({
            companyName
        }),
        mainAction: /*#__PURE__*/ _jsx(IntegrationCard.MainActionButton, {
            bi: {
                name: 'manage_xero_connection'
            },
            testId: testIds.Manage(),
            onClick: handleManage,
            children: xeroTexts.manageConnection()
        })
    };
};
export default function XeroCard() {
    const { t: xeroTexts } = useLocaleKeys('integrations.xero');
    const { startLoader, stopLoader } = useOverlayContext();
    const { browser: { changeLocation } } = useIoContext();
    const { api, logBI } = useStoreActions();
    const { integrations, refresh: refreshIntegrations } = useIntegrations({
        enabled: false
    });
    const navigate = useNavigate();
    const { XeroCardTID } = useMemo(()=>GetXeroCardTID(), []);
    const currentXeroIntegration = integrationModel.xeroIntegration(integrations);
    useBusinessChannelEvent({
        source: 'mgmt',
        eventName: PusherEvent.IntegrationEvent,
        callback: onPusherIntegrationEvent
    });
    if (!currentXeroIntegration) {
        return null;
    }
    const handleConnect = async ()=>{
        logBI(18600);
        startLoader();
        const integration = await api.xero.connect({
            redirectUrl: "http://".concat(window.location.host).concat(routes.availableApps)
        });
        if (integration.linkUrl) {
            const connectUrl = new URL(integration.linkUrl);
            changeLocation(connectUrl.toString());
        } else {
            stopLoader();
        }
    };
    const handleManage = async ()=>{
        logBI(18601);
        navigate(routes.xeroSetup);
    };
    function onPusherIntegrationEvent() {
        refreshIntegrations({
            throwOnError: false
        });
    }
    const integrationCardProps = ()=>{
        switch(currentXeroIntegration.status){
            case IntegrationStatus.Linked:
                {
                    return currentXeroIntegration.isReady() ? manageCardProps({
                        companyName: currentXeroIntegration.companyName() || 'your account',
                        handleManage,
                        xeroTexts,
                        testIds: XeroCardTID
                    }) : connectionInProgressCardProps({
                        xeroTexts,
                        testIds: XeroCardTID
                    });
                }
            case IntegrationStatus.Unlinked:
                {
                    return connectCardProps({
                        handleConnect,
                        xeroTexts,
                        testIds: XeroCardTID
                    });
                }
            default:
                {
                    return connectCardProps({
                        handleConnect,
                        xeroTexts,
                        testIds: XeroCardTID
                    });
                }
        }
    };
    return /*#__PURE__*/ _jsx(IntegrationCard, _object_spread({
        testId: XeroCardTID.Card(),
        icon: /*#__PURE__*/ _jsx(XeroLogo, {
            width: 40,
            height: 40
        }),
        title: xeroTexts.title()
    }, integrationCardProps()));
}
